import { connect, FormikValues } from 'formik'
import { IntlShape, useIntl } from 'react-intl'
import styled from 'styled-components'
import { string } from 'yup'

import { Radio } from '@/components/Form'
import Label from '@/components/Label'
import { FormLabel, Row } from '@/components/StyledComponents'
import Toggle from '@/components/Toggle'
import { VariableInputContainer } from '@/pages/SpaceAppBuilder/components/StyledComponents'
import AutoCompleteVariableInput from '@/pages/SpaceAppBuilder/components/VariableInputDropdown'
import { useWorkflowFeature } from '@/pages/SpaceAppBuilder/FeaturesPage/WorkflowFeatureContext'
import { DateFormatterStepData, FieldTypes, StepData, Variable } from '@/pages/SpaceAppBuilder/utilities/interfaces'

import messages from './messages'

//<editor-fold desc="Interfaces">
enum DateFormatterInputType {
  variable = 'variable',
  currentDate = 'current-datetime'
}

enum DateFormatterFormatType {
  long = 'long',
  short = 'short'
}

type DateTimeFormatterProps = {
  readOnly?: boolean
}
//</editor-fold>

// TODO make the StyledRow a common component for Operations
//<editor-fold desc="Styled Components">
const StyledRow = styled(Row)`
  justify-content: flex-start;
  margin-bottom: 5px;
`

const StyledAutoCompleteVariableInput = styled(AutoCompleteVariableInput)`
  ${VariableInputContainer} {
    padding: 0;
  }
`

//</editor-fold>

/**
 * Get the date string, this is what the client side adaptive card renders use
 *
 * @param {Object} opts
 * @param {Date} [opts.date = new Date()] - The date to format
 * @param {'long' | 'short'} [opts.format = 'long'] - long or short date format
 * @returns string
 */
const getDateString = ({ date = new Date(), format = 'long' }: { date?: Date; format?: 'long' | 'short' } = {}) => {
  return date.toLocaleDateString('en-US', { day: 'numeric', weekday: format, month: format, year: 'numeric' })
}

const getValidationSchema = (intl: IntlShape, formObject: any) => {
  if (formObject.dateFormatFunctionType === DateFormatterInputType.variable) {
    return {
      dateInputVariable: string().required(intl.formatMessage(messages.dateTimeFormatVariableRequired))
    }
  }

  return {}
}

const getInitialValues = (stepData: DateFormatterStepData): StepData => ({
  dateFormatFunctionType: stepData.dateFormatFunctionType ?? DateFormatterInputType.variable,
  dateInputVariable: stepData.dateInputVariable ?? '',
  dateInputVariableDisplayName: stepData.dateInputVariableDisplayName ?? '',
  format: stepData.format ?? 'long',
  includeTime: stepData.includeTime ?? false
})

const getDateFormatterOperationHelpUrl =
  'https://help.workgrid.com/s/article/Utilizing-the-Date-Time-Formatter-Operation'
const DateFormatterOperation = ({ formik, readOnly = false }: DateTimeFormatterProps & FormikValues) => {
  const intl = useIntl()

  const { getVariablesForStep } = useWorkflowFeature()

  const variables = getVariablesForStep()

  const onSelectVariable = (selectedVariable: Variable, formikName: string) => {
    // Auto select the 'variable' radio button as well when this changes
    formik.setFieldValue('stepData.dateFormatFunctionType', DateFormatterInputType.variable)
    formik.setFieldValue(formikName, selectedVariable.name)
    formik.setFieldValue(`${formikName}DisplayName`, selectedVariable.displayName)
  }

  const clearVariables = () => {
    formik.setFieldValue('stepData.dateInputVariable', '')
    formik.setFieldValue('stepData.dateInputVariableDisplayName', '')
  }

  return (
    <>
      <StyledRow>
        <Label required>{intl.formatMessage(messages.dateTimeFormatterVariableInputLabel)}</Label>
      </StyledRow>
      <Radio>
        <Radio.Input
          disabled={readOnly}
          name={'stepData.dateFormatFunctionType'}
          value={DateFormatterInputType.variable}
          data-testid={'variable-function-type-radio'}
        >
          <StyledAutoCompleteVariableInput
            readOnly={readOnly}
            name={'stepData.dateInputVariable'}
            variables={variables}
            placeholder={intl.formatMessage(messages.dateTimeFormatterPlaceholder)}
            showValidationMessage={true}
            validVariableTypes={[FieldTypes.STRING, FieldTypes.ANY]}
            onSelectOption={onSelectVariable}
          />
        </Radio.Input>
        <Radio.Input
          disabled={readOnly}
          name={'stepData.dateFormatFunctionType'}
          label={'Current Date'}
          value={DateFormatterInputType.currentDate}
          onChange={clearVariables}
        />
      </Radio>
      <StyledRow>
        <FormLabel>{intl.formatMessage(messages.dateTimeFormatterFormatInputLabel)}</FormLabel>
      </StyledRow>
      <Radio>
        <Radio.Input
          disabled={readOnly}
          id={'stepData.format.long'}
          name={'stepData.format'}
          value={DateFormatterFormatType.long}
          label={intl.formatMessage(messages.dateTimeFormatterLongLabel, {
            todayAsLong: getDateString({ format: DateFormatterFormatType.long })
          })}
        />
        <Radio.Input
          disabled={readOnly}
          id={'stepData.format.short'}
          name={'stepData.format'}
          value={DateFormatterFormatType.short}
          label={intl.formatMessage(messages.dateTimeFormatterShortLabel, {
            todayAsShort: getDateString({ format: DateFormatterFormatType.short })
          })}
        />
      </Radio>
      <Toggle
        label={intl.formatMessage(messages.dateTimeFormatterIncludeTimeLabel, {
          time: new Date().toLocaleTimeString()
        })}
        disabled={readOnly}
        name={'stepData.includeTime'}
        checked={formik.values.stepData.includeTime}
        ariaLabel={intl.formatMessage(messages.dateTimeFormatterIncludeTimeLabel, {
          time: new Date().toLocaleTimeString()
        })}
        onChange={() => {
          formik.setFieldValue('stepData.includeTime', !formik.values.stepData.includeTime)
        }}
        formik={formik}
      ></Toggle>
    </>
  )
}

DateFormatterOperation.displayName = 'DateFormatterOperation'

export default connect(DateFormatterOperation)

export { getInitialValues, getValidationSchema, getDateFormatterOperationHelpUrl }
